import React, {useEffect, useState} from 'react';
import AppConstants from '../../Constants/AppConstants';
import {AppToast} from '../../HelperClasses/AppToast';
import {useLocale} from '../../Localization/LocaleContext';
import {HR} from '../hr.service';
import {adminApi} from '../../Component/PageKit/adminApi';
import {
  DataTable,
  DetailModal,
  FormModal,
  AppPage,
  PrimaryButton,
  RowAction,
  SecondaryButton,
  formatDate,
  formatMoney,
  humanize,
  lookup,
  staffName,
  useApiList,
  useTabParam,
} from '../../Component/PageKit/PageKit';

const TABS = [
  {key: 'staff', label: 'people.tab.staff'},
  {key: 'departments', label: 'people.tab.departments'},
  {key: 'job-profiles', label: 'people.tab.job_profiles'},
  {key: 'pay-grades', label: 'people.tab.pay_grades'},
];

const ADD_LABEL = {
  staff: 'people.action.add_staff',
  departments: 'people.action.add_department',
  'job-profiles': 'people.action.add_job_profile',
  'pay-grades': 'people.action.add_pay_grade',
};

const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contractor'].map(value => ({
  value,
  label: humanize(value),
}));

const PER_PAGE = 20;

// API failures or transitional payloads must not make the whole People route
// crash. Keep each list renderable even while its request is loading/failing.
const listItems = list => (Array.isArray(list?.items) ? list.items : []);

const toOptions = (items, labelKey) => (Array.isArray(items) ? items : []).map(item => ({value: item.id, label: item[labelKey] || `#${item.id}`}));

const payGradeLabel = (grade, t = (key, fallback) => fallback) => {
  if (!grade) return '—';
  const currency = grade.currency || grade.currency_code;
  const min = formatMoney(grade.min_salary ?? grade.base_salary, currency);
  const max = grade.max_salary === undefined || grade.max_salary === null
    ? null
    : formatMoney(grade.max_salary, currency);
  const range = max ? `${min} – ${max}` : min;
  return grade.currency_id && !currency ? `${range} · ${t('people.currency_id', 'Currency')} #${grade.currency_id}` : range;
};

const PeopleOnboarding = ({onAddStaff}) => {
  const {translate: t} = useLocale();
  return (
  <div className="mb-5 rounded-2xl border p-5 md:p-6" style={{borderColor: 'var(--color-border)', background: 'linear-gradient(135deg, color-mix(in srgb, var(--color-accent) 12%, var(--color-surface)), var(--color-surface))'}}>
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{color: 'var(--color-accent)'}}>{t('people.onboarding.eyebrow')}</p>
        <h2 className="mt-2 text-xl font-semibold" style={{color: 'var(--color-text)'}}>{t('people.onboarding.title')}</h2>
        <p className="mt-1 max-w-2xl text-sm" style={{color: 'var(--color-text-secondary)'}}>
          {t('people.onboarding.description')}
        </p>
      </div>
      <PrimaryButton onClick={onAddStaff}>{t('people.onboarding.add_first')}</PrimaryButton>
    </div>
    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
      {[
        ['01', t('people.onboarding.step_staff'), t('people.onboarding.step_staff_description')],
        ['02', t('people.onboarding.step_teams'), t('people.onboarding.step_teams_description')],
        ['03', t('people.onboarding.step_pay'), t('people.onboarding.step_pay_description')],
      ].map(([number, title, description]) => (
        <div key={number} className="rounded-xl border p-4" style={{borderColor: 'var(--color-border)', background: 'color-mix(in srgb, var(--color-surface) 78%, transparent)'}}>
          <div className="text-xs font-bold" style={{color: 'var(--color-accent)'}}>{number}</div>
          <div className="mt-2 font-semibold" style={{color: 'var(--color-text)'}}>{title}</div>
          <div className="mt-1 text-xs leading-5" style={{color: 'var(--color-text-secondary)'}}>{description}</div>
        </div>
      ))}
    </div>
  </div>
  );
};

const StaffDetail = ({id, departments, jobProfiles, payGrades, onClose}) => {
  const {translate: t, locale} = useLocale();
  const [staff, setStaff] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.get(`${HR.staff}/${id}`).then(setStaff).catch(e => setError(e.message));
  }, [id]);

  const rows = staff
    ? [
        [t('common.name'), staffName(staff)],
        [t('people.field.employee_code'), staff.employee_code],
        [t('people.field.job_title'), staff.job_title],
        [t('people.field.department'), lookup(departments, staff.department_id, d => d.name)],
        [t('people.field.job_profile'), lookup(jobProfiles, staff.job_profile_id, j => j.title)],
        [t('people.field.pay_grade'), lookup(payGrades, staff.pay_grade_id, p => `${p.name} · ${payGradeLabel(p, t)}`)],
        [t('people.field.employment_type'), t(`people.employment.${staff.employment_type}`, humanize(staff.employment_type))],
        [t('people.field.start_date'), formatDate(staff.start_date, locale)],
        [t('people.field.user_id'), staff.user_id],
      ]
    : [];

  return (
    <DetailModal
      title={staff ? staffName(staff) : t('people.staff_profile')}
      rows={rows}
      loading={!staff && !error}
      error={error}
      onClose={onClose}
    />
  );
};

const PeoplePage = () => {
  const {translate: t, locale} = useLocale();
  const [tab, setTab] = useTabParam(TABS, 'staff');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const staff = useApiList(`${HR.staff}?page=${page}&per_page=${PER_PAGE}`);
  const departments = useApiList(HR.departments);
  const jobProfiles = useApiList(HR.jobProfiles);
  const payGrades = useApiList(HR.payGrades);

  const staffItems = listItems(staff);
  const departmentItems = listItems(departments);
  const jobProfileItems = listItems(jobProfiles);
  const payGradeItems = listItems(payGrades);

  const departmentOptions = toOptions(departmentItems, 'name');
  const jobProfileOptions = toOptions(jobProfileItems, 'title');
  const payGradeOptions = payGradeItems.map(p => ({
    value: p.id,
    label: `${p.name} · ${payGradeLabel(p, t)}`,
  }));

  const modalKey = typeof modal === 'string' ? modal : modal?.type;
  // `typeof null` is "object"; guard the initial closed-modal state before
  // reading the selected staff record.
  const editing = modal && typeof modal === 'object' ? modal.item : null;
  const save = (path, reload, createMessage, updateMessage) => async payload => {
    if (editing?.id) {
      await adminApi.put(`${path}/${editing.id}`, payload);
      AppToast('success', t(updateMessage));
    } else {
      await adminApi.post(path, payload);
      AppToast('success', t(createMessage));
    }
    reload();
  };

  const forms = {
    staff: {
      title: editing ? 'people.form.staff.edit' : 'people.form.staff.add',
      description: 'people.form.staff.description',
      submitLabel: editing ? 'common.save_changes' : 'people.action.add_staff',
      initialValues: editing || {},
      fields: [
        {name: 'user_id', label: 'people.field.user', type: 'user', required: true, numeric: true},
        {name: 'employee_code', label: 'people.field.employee_code', placeholder: 'people.placeholder.employee_code', half: true},
        {name: 'job_title', label: 'people.field.job_title', placeholder: 'people.placeholder.job_title', half: true},
        {name: 'department_id', label: 'people.field.department', type: 'select', options: departmentOptions, numeric: true, half: true},
        {name: 'job_profile_id', label: 'people.field.job_profile', type: 'select', options: jobProfileOptions, numeric: true, half: true},
        {name: 'pay_grade_id', label: 'people.field.pay_grade', type: 'select', options: payGradeOptions, numeric: true, half: true},
        {name: 'employment_type', label: 'people.field.employment_type', type: 'select', options: EMPLOYMENT_TYPES.map(option => ({...option, label: `people.employment.${option.value}`})), defaultValue: 'full_time', half: true},
        {name: 'start_date', label: 'people.field.start_date', type: 'date', half: true},
      ],
      onSubmit: save(HR.staff, staff.reload, 'people.toast.staff_added', 'people.toast.staff_updated'),
    },
    departments: {
      title: editing ? 'people.form.department.edit' : 'people.form.department.add',
      submitLabel: editing ? 'common.save_changes' : 'people.action.add_department',
      initialValues: editing || {},
      fields: [
        {name: 'name', label: 'common.name', required: true, placeholder: 'people.placeholder.department'},
        {name: 'description', label: 'common.description', type: 'textarea', placeholder: 'people.placeholder.department_description'},
      ],
      onSubmit: save(HR.departments, departments.reload, 'people.toast.department_added', 'people.toast.department_updated'),
    },
    'job-profiles': {
      title: editing ? 'people.form.job_profile.edit' : 'people.form.job_profile.add',
      submitLabel: editing ? 'common.save_changes' : 'people.action.add_job_profile',
      initialValues: editing || {},
      fields: [
        {name: 'title', label: 'common.title', required: true, placeholder: 'people.placeholder.job_title'},
        {name: 'department_id', label: 'people.field.department', type: 'select', options: departmentOptions, numeric: true},
        {name: 'description', label: 'common.description', type: 'textarea'},
      ],
      onSubmit: save(HR.jobProfiles, jobProfiles.reload, 'people.toast.job_profile_added', 'people.toast.job_profile_updated'),
    },
    'pay-grades': {
      title: editing ? 'people.form.pay_grade.edit' : 'people.form.pay_grade.add',
      submitLabel: editing ? 'common.save_changes' : 'people.action.add_pay_grade',
      initialValues: editing || {},
      fields: [
        {name: 'name', label: 'common.name', required: true, placeholder: 'people.placeholder.pay_grade'},
        {name: 'min_salary', label: 'people.field.minimum_salary', type: 'number', required: true, min: 0, half: true},
        {name: 'max_salary', label: 'people.field.maximum_salary', type: 'number', required: true, min: 0, half: true},
        {name: 'currency_id', label: 'people.field.currency_id', type: 'number', required: true, min: 1, half: true},
      ],
      onSubmit: save(HR.payGrades, payGrades.reload, 'people.toast.pay_grade_added', 'people.toast.pay_grade_updated'),
    },
  };

  const departmentName = id => lookup(departmentItems, id, d => d.name);

  return (
    <AppPage
      title="people.title"
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
      action={<PrimaryButton onClick={() => setModal(tab)}>{t(ADD_LABEL[tab])}</PrimaryButton>}>
      {tab === 'staff' && (
        <>
          {!staff.loading && !staff.error && staffItems.length === 0 && (
            <PeopleOnboarding onAddStaff={() => setModal('staff')} />
          )}
          <DataTable
            columns={[
              {key: 'name', label: 'common.name', render: staffName},
              {key: 'employee_code', label: 'people.field.employee_code', muted: true},
              {key: 'job_title', label: 'people.field.job_title'},
              {key: 'department', label: 'people.field.department', render: s => departmentName(s.department_id)},
              {key: 'employment_type', label: 'people.field.employment_type', render: s => t(`people.employment.${s.employment_type}`, humanize(s.employment_type))},
              {key: 'start_date', label: 'people.field.start_date', muted: true, render: s => formatDate(s.start_date, locale)},
              {key: 'actions', label: '', align: 'right', render: s => <RowAction onClick={() => setModal({type: 'staff', item: s})}>{t('common.edit', 'Edit')}</RowAction>},
            ]}
            rows={staffItems}
            loading={staff.loading}
            error={staff.error}
            emptyText="people.empty.staff"
            onRowClick={s => setDetailId(s.id)}
          />
          {(page > 1 || staffItems.length === PER_PAGE) && (
            <div className="flex items-center justify-end gap-2 mt-3">
              <SecondaryButton disabled={page === 1} onClick={() => setPage(p => p - 1)}>{t('common.previous', 'Previous')}</SecondaryButton>
              <span className="text-xs px-2" style={{color: 'var(--color-text-secondary)'}}>{t('people.pagination.page', {page})}</span>
              <SecondaryButton disabled={staffItems.length < PER_PAGE} onClick={() => setPage(p => p + 1)}>{t('common.next', 'Next')}</SecondaryButton>
            </div>
          )}
        </>
      )}

      {tab === 'departments' && (
        <DataTable
          columns={[
            {key: 'name', label: 'common.name'},
            {key: 'description', label: 'common.description', muted: true},
            {key: 'actions', label: '', align: 'right', render: d => <RowAction onClick={() => setModal({type: 'departments', item: d})}>{t('common.edit', 'Edit')}</RowAction>},
          ]}
          rows={departmentItems}
          loading={departments.loading}
          error={departments.error}
          emptyText="people.empty.departments"
        />
      )}

      {tab === 'job-profiles' && (
        <DataTable
          columns={[
            {key: 'title', label: 'common.title'},
            {key: 'department', label: 'people.field.department', render: j => departmentName(j.department_id)},
            {key: 'description', label: 'common.description', muted: true},
            {key: 'actions', label: '', align: 'right', render: j => <RowAction onClick={() => setModal({type: 'job-profiles', item: j})}>{t('common.edit', 'Edit')}</RowAction>},
          ]}
          rows={jobProfileItems}
          loading={jobProfiles.loading}
          error={jobProfiles.error}
          emptyText="people.empty.job_profiles"
        />
      )}

      {tab === 'pay-grades' && (
        <DataTable
          columns={[
            {key: 'name', label: 'people.field.grade'},
            {key: 'salary', label: 'people.field.salary_range', align: 'right', render: p => payGradeLabel(p, t)},
            {key: 'actions', label: '', align: 'right', render: p => <RowAction onClick={() => setModal({type: 'pay-grades', item: p})}>{t('common.edit', 'Edit')}</RowAction>},
          ]}
          rows={payGradeItems}
          loading={payGrades.loading}
          error={payGrades.error}
          emptyText="people.empty.pay_grades"
        />
      )}

      {modal && <FormModal {...forms[modalKey]} draftKey={`arcticwhale:${AppConstants.tenantName || 'current-tenant'}:${AppConstants.userId || 'current-user'}:people:${modalKey}:${editing?.id || 'new'}`} resetLabel="common.reset" onClose={() => setModal(null)} />}
      {detailId && (
        <StaffDetail
          id={detailId}
          departments={departmentItems}
          jobProfiles={jobProfileItems}
          payGrades={payGradeItems}
          onClose={() => setDetailId(null)}
        />
      )}
    </AppPage>
  );
};

export default PeoplePage;
